import { ConfigValues } from '../configManager';

/**
 * Type guard to check if a value is a ConfigValues object
 */
export const isConfigObject = (value: string | string[] | number | boolean | ConfigValues): value is ConfigValues => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/**
 * Safely get a nested property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property (e.g., 'register.nameservers.custom')
 * @returns The value at the path or undefined if not found
 */
export const getNestedConfig = (config: ConfigValues, path: string): string | string[] | number | boolean | ConfigValues | ConfigValues[] | undefined => {
    const keys = path.split('.');
    let current: string | string[] | number | boolean | ConfigValues | ConfigValues[] = config;

    for (const key of keys) {
        if (Array.isArray(current) || !isConfigObject(current) || !(key in current)) {
            return undefined;
        }

        current = current[key];
    }

    return current;
};

/**
 * Safely get a nested boolean property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property (e.g., 'register.nameservers.custom')
 * @param defaultValue - Default value if not found or not boolean
 * @returns The boolean value or default
 */
export const getNestedBoolean = (config: ConfigValues, path: string, defaultValue = false): boolean => {
    const value = getNestedConfig(config, path);
    return typeof value === 'boolean' ? value : defaultValue;
};

/**
 * Safely get a nested string property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property
 * @param defaultValue - Default value if not found or not string
 * @returns The string value or default
 */
export const getNestedString = (config: ConfigValues, path: string, defaultValue = ''): string => {
    const value = getNestedConfig(config, path);
    return typeof value === 'string' ? value : defaultValue;
};

/**
 * Safely get a nested number property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property
 * @param defaultValue - Default value if not found or not number
 * @returns The number value or default
 */
export const getNestedNumber = (config: ConfigValues, path: string, defaultValue = 0): number => {
    const value = getNestedConfig(config, path);
    return typeof value === 'number' ? value : defaultValue;
};

/**
 * Safely get a nested object property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property
 * @param defaultValue - Default value if not found or not object
 * @returns The object value or default
 */
export const getNestedObject = (config: ConfigValues, path: string, defaultValue: ConfigValues = {}): ConfigValues => {
    const value = getNestedConfig(config, path);
    return !Array.isArray(value) && isConfigObject(value) ? value : defaultValue;
};

/**
 * Safely get a nested array property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property
 * @param defaultValue - Default value if not found or not array
 * @returns The array value or default
 */
export const getNestedArray = <T = string | number | boolean | ConfigValues>(config: ConfigValues, path: string, defaultValue: T[] = []): T[] => {
    const value = getNestedConfig(config, path);
    return Array.isArray(value) ? (value as T[]) : defaultValue;
};
